import { openDB, type DBSchema } from 'idb'
import type { CompletedPhotoFraming, PhotoFramingScope } from '@/api/photoFraming'
export interface PendingPhotoFrame { id: string; actorId: string; scope: PhotoFramingScope; framing: CompletedPhotoFraming }
interface PhotoQueueDB extends DBSchema { pending: { key: string; value: PendingPhotoFrame } }
export interface PhotoQueueStorage { put(row: PendingPhotoFrame): Promise<void>; all(): Promise<PendingPhotoFrame[]>; remove(id: string): Promise<void> }
let database: ReturnType<typeof openDB<PhotoQueueDB>> | undefined
const db = () => database ||= openDB<PhotoQueueDB>('coachsight-photo-framing', 1, { upgrade(db) { db.createObjectStore('pending', { keyPath: 'id' }) } })
export const photoQueueStorage: PhotoQueueStorage = {
  async put(row) { await (await db()).put('pending', row) },
  async all() { return (await db()).getAll('pending') },
  async remove(id) { await (await db()).delete('pending', id) },
}
export function pendingPhotoFrame(actorId: string, scope: PhotoFramingScope, framing: CompletedPhotoFraming): PendingPhotoFrame {
  return { id: JSON.stringify([actorId, scope.eventId, scope.participantId, framing.imageVersion, framing.algorithmVersion]), actorId, scope: { ...scope }, framing: { ...framing, face: framing.face ? { ...framing.face } : null } }
}
/** Metadata-only queue. Never mixes photo updates into the assessment outbox. */
export async function flushPhotoFrames(options: {
  storage: PhotoQueueStorage
  identity: () => { actorId: string; token: string } | null
  online: () => boolean
  send: (row: PendingPhotoFrame, token: string) => Promise<unknown>
}) {
  const identity = options.identity()
  if (!identity || !options.online()) return
  for (const row of await options.storage.all()) {
    const now = options.identity()
    if (!options.online() || now?.actorId !== identity.actorId || now.token !== identity.token) return
    if (row.actorId !== identity.actorId) continue
    try {
      await options.send(row, identity.token)
      await options.storage.remove(row.id)
    } catch (error) {
      const { status, code } = error as { status?: number; code?: string }
      const removedResource = status === 404 && ['PARTICIPANT_NOT_FOUND', 'EVENT_NOT_FOUND', 'TRYOUT_NOT_FOUND', 'SEASON_NOT_FOUND', 'CLUB_NOT_FOUND'].includes(code || '')
      // Obsolete image, withdrawn access, removed participant or invalid metadata.
      // These derived results can be recalculated; never touch score storage.
      if (status === 403 || status === 409 || status === 422 || removedResource) await options.storage.remove(row.id)
      else return // offline, expired auth, unavailable API: retain for later retry
    }
  }
}
